import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "../app/store";
import {
    fetchGiftCategories,
    addGiftCategory,
    updateGiftCategory,
    deleteGiftCategory,
    resetCreateStatus,
    resetUpdateStatus,
    resetDeleteStatus,
    resetListStatus,
} from "../features/giftCategoriesSlice";
import useConfirmation from "../components/confirm_modal";
import { FiEdit2, FiTrash2, FiRefreshCw, FiPlus, FiX } from "react-icons/fi";

/* ====================== Styled ====================== */
const Page = styled.main`
  padding: 16px;
`;

const Card = styled.section`
  background: #fff;
  border: 1px solid #dfe3ea;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
`;

const Toolbar = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid #e6e9f0;
`;

const Title = styled.h2`
  margin: 0;
  font: 600 18px/1.3 Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
  color: #111827;
`;

const ToolbarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Btn = styled.button<{ variant?: "primary" | "ghost" | "danger"; small?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  padding: ${({ small }) => (small ? "6px 10px" : "8px 12px")};
  border: 1px solid ${({ variant }) =>
        variant === "primary" ? "#2563eb" : variant === "danger" ? "#ef4444" : "#d1d5db"};
  background: ${({ variant }) =>
        variant === "primary" ? "#2563eb" : "#fff"};
  color: ${({ variant }) => (variant === "primary" ? "#fff" : "#111827")};
  cursor: pointer;
  font: 600 13px/1 Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
  transition: transform 0.08s ease, opacity 0.2s ease, background 0.2s ease;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:active { transform: translateY(1px); }
`;

const Table = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 0.8fr 0.8fr 0.7fr 1.4fr 110px;
  row-gap: 0;
  column-gap: 0;
  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const TH = styled.div`
  padding: 10px 12px;
  font-weight: 600;
  font-size: 13px;
  color: #374151;
  background: #f8fafc;
  border-bottom: 1px solid #e6e9f0;
  @media (max-width: 980px) {
    display: none;
  }
`;

const TR = styled.div`
  display: contents;
  @media (max-width: 980px) {
    display: block;
    border-bottom: 1px solid #edf0f5;
  }
`;

const TD = styled.div`
  padding: 12px;
  font-size: 14px;
  color: #111827;
  border-bottom: 1px solid #f1f3f7;
  word-break: break-word;

  @media (max-width: 980px) {
    display: grid;
    grid-template-columns: 140px 1fr;
    gap: 8px;
    &:nth-child(1)::before { content: "Name"; color: #6b7280; }
    &:nth-child(2)::before { content: "Min Range"; color: #6b7280; }
    &:nth-child(3)::before { content: "Max Range"; color: #6b7280; }
    &:nth-child(4)::before { content: "Currency"; color: #6b7280; }
    &:nth-child(5)::before { content: "Meta"; color: #6b7280; }
  }

  small { color: #6b7280; }
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: flex-start;
`;

const Err = styled.div`
  color: #b91c1c;
  background: #fef2f2;
  border: 1px solid #fee2e2;
  border-radius: 8px;
  padding: 8px 10px;
  margin: 10px 16px;
  font-size: 13px;
`;

/* Modal basics */
const Backdrop = styled.div`
  position: fixed; inset: 0; background: rgba(17,24,39,.5);
  display: flex; align-items: center; justify-content: center; z-index: 50;
`;
const Modal = styled.div`
  width: min(700px, 92vw);
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0,0,0,.12);
`;

const ModalHeader = styled.div`
  padding: 14px 16px;
  border-bottom: 1px solid #e6e9f0;
  display: flex; align-items: center; justify-content: space-between;
`;
const ModalTitle = styled.h3`
  margin: 0; font: 600 16px/1.3 Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
  color: #111827;
`;
const ModalBody = styled.div`
  padding: 16px;
`;
const ModalFooter = styled.div`
  padding: 12px 16px;
  border-top: 1px solid #e6e9f0;
  display: flex; gap: 10px; justify-content: flex-end;
`;

const FormGrid = styled.div`
  display: grid; gap: 12px;
`;
const Field = styled.label`
  display: grid; gap: 6px;
  font-size: 13px; color: #374151;
`;
const Input = styled.input`
  background: #fff;
  border: 1px solid #d1d5db;
  color: #111827;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  &:focus { outline: none; border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,.15); }
`;
const Grid2 = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 12px;
  @media (max-width: 720px) { grid-template-columns: 1fr; }
`;
const FieldErr = styled.div`
  color: #b91c1c; font-size: 12px;
`;

const IconBtn = styled.button<{ variant?: "ghost" | "danger" }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid
    ${({ variant }) => (variant === "danger" ? "#fca5a5" : "#d1d5db")};
  background: #fff;
  color: ${({ variant }) => (variant === "danger" ? "#b91c1c" : "#111827")};
  cursor: pointer;
  transition: transform .08s ease, background .2s ease, border-color .2s ease, opacity .2s ease;

  &:hover {
    background: ${({ variant }) => (variant === "danger" ? "#fef2f2" : "#f8fafc")};
    border-color: ${({ variant }) => (variant === "danger" ? "#ef4444" : "#cbd5e1")};
  }
  &:active { transform: translateY(1px); }
  &:disabled { opacity: .55; cursor: not-allowed; }
  svg { display:block; } /* crisp centering */
`;

const CloseIconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #111827;
  cursor: pointer;
  transition: transform .08s ease, background .2s ease, border-color .2s ease, opacity .2s ease;
  &:hover { background:#f8fafc; border-color:#cbd5e1; }
  &:active { transform: translateY(1px); }
  &:focus-visible { outline: 2px solid #2563eb; outline-offset: 2px; }
  svg { display:block; }
`;


/* ====================== Types & helpers ====================== */
type CategoryInput = {
    name: string;
    description: string;
    min_range: number | string;
    max_range: number | string;
    currency: string;
};
type ValidationErrorItem = { loc: (string | number)[]; msg: string; type: string; };

const emptyForm: CategoryInput = { name: "", description: "", min_range: "", max_range: "", currency: "USD" };

function toNumber(n: number | string): number {
    if (typeof n === "number") return n;
    const v = String(n).trim();
    if (!v) return 0;
    const parsed = Number(v);
    return Number.isNaN(parsed) ? 0 : parsed;
}

function mapValidation(items?: ValidationErrorItem[] | null) {
    const map: Record<string, string> = {};
    if (!items) return map;
    for (const it of items) {
        const key = Array.isArray(it.loc) ? it.loc.join(".") : String(it.loc);
        map[key] = it.msg;
    }
    return map;
}

/* ====================== Component ====================== */
const GiftCategoriesPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { confirm, ConfirmationModal } = useConfirmation();

    const {
        categories,
        // list
        listStatus, listError, isListInProgress,
        // create
        createStatus, createError, isCreateInProgress, createValidationErrors,
        // update
        updateStatus, updateError, isUpdateInProgress, updateValidationErrors,
        // delete
        deleteStatus, deleteError, isDeleteInProgress,
    } = useSelector((s: RootState) => s.giftCategories);

    const [addOpen, setAddOpen] = useState(false);
    const [addForm, setAddForm] = useState<CategoryInput>(emptyForm);

    const [editOpen, setEditOpen] = useState(false);
    const [editId, setEditId] = useState<string | null>(null);
    const [editForm, setEditForm] = useState<CategoryInput>(emptyForm);

    const createErrs = useMemo(() => mapValidation(createValidationErrors), [createValidationErrors]);
    const updateErrs = useMemo(() => mapValidation(updateValidationErrors), [updateValidationErrors]);

    useEffect(() => {
        dispatch(fetchGiftCategories());
        return () => {
            dispatch(resetListStatus());
            dispatch(resetCreateStatus());
            dispatch(resetUpdateStatus());
            dispatch(resetDeleteStatus());
        };
    }, [dispatch]);

    /* ---------- Add ---------- */
    const handleAdd = async (e: React.FormEvent) => {
        e.preventDefault();
        const minV = toNumber(addForm.min_range);
        const maxV = toNumber(addForm.max_range);
        if (!addForm.name || !addForm.currency) return;
        if (minV > maxV) { alert("Min range cannot be greater than Max range."); return; }

        const action = await dispatch(addGiftCategory({
            name: addForm.name.trim(),
            description: addForm.description.trim(),
            min_range: minV,
            max_range: maxV,
            currency: addForm.currency.trim(),
        }));

        if ((action as any).meta.requestStatus === "fulfilled") {
            setAddOpen(false);
            setAddForm(emptyForm);
        }
    };

    /* ---------- Edit ---------- */
    const openEdit = (id: string) => {
        const item = categories.find(c => c.id === id);
        if (!item) return;
        setEditId(id);
        setEditForm({
            name: item.name,
            description: item.description,
            min_range: item.min_range,
            max_range: item.max_range,
            currency: item.currency,
        });
        setEditOpen(true);
    };

    const handleUpdate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editId) return;
        const minV = toNumber(editForm.min_range);
        const maxV = toNumber(editForm.max_range);
        if (minV > maxV) { alert("Min range cannot be greater than Max range."); return; }

        const action = await dispatch(updateGiftCategory({
            category_id: editId,
            data: {
                name: String(editForm.name).trim(),
                description: String(editForm.description).trim(),
                min_range: minV,
                max_range: maxV,
                currency: String(editForm.currency).trim(),
            }
        }));

        if ((action as any).meta.requestStatus === "fulfilled") {
            setEditOpen(false);
            setEditId(null);
            setEditForm(emptyForm);
        }
    };

    /* ---------- Delete ---------- */
    const handleDelete = async (id: string) => {
        const ok = await confirm("Delete this category? Do you want to proceed?");
        if (!ok) return;
        await dispatch(deleteGiftCategory({ category_id: id }));
    };

    /* ====================== UI ====================== */
    return (
        <Page>
            <Card>
                <Toolbar>
                    <Title>Gift Categories</Title>
                    <ToolbarActions>
                        {deleteError && <Err style={{ margin: 0 }}>{deleteError}</Err>}
                        <Btn onClick={() => dispatch(fetchGiftCategories())} disabled={isListInProgress}>
                            <FiRefreshCw /> {isListInProgress ? "Refreshing..." : "Refresh"}
                        </Btn>
                        <Btn variant="primary" onClick={() => setAddOpen(true)}>
                            <FiPlus /> Add Category
                        </Btn>
                    </ToolbarActions>
                </Toolbar>

                {listError && <Err>Failed to load: {listError}</Err>}

                {/* Table Header */}
                <Table>
                    <TH>Name</TH>
                    <TH>Min Range</TH>
                    <TH>Max Range</TH>
                    <TH>Currency</TH>
                    <TH>Meta</TH>
                    <TH>Actions</TH>

                    {/* Rows */}
                    {categories.map((c) => (
                        <TR key={c.id}>
                            <TD>
                                <div style={{ fontWeight: 600 }}>{c.name}</div>
                                {c.description && <small>{c.description}</small>}
                            </TD>
                            <TD>{c.min_range}</TD>
                            <TD>{c.max_range}</TD>
                            <TD>{c.currency}</TD>
                            <TD>
                                <small>Created: {new Date(c.created_at).toLocaleString()}</small><br />
                                <small>Updated: {new Date(c.updated_at).toLocaleString()}</small>
                            </TD>
                            <TD>
                                <Actions>
                                    <IconBtn
                                        variant="ghost"
                                        onClick={() => openEdit(c.id)}
                                        aria-label="Edit category"
                                        title="Edit"
                                    >
                                        <FiEdit2 size={16} />
                                    </IconBtn>

                                    <IconBtn
                                        variant="danger"
                                        onClick={() => handleDelete(c.id)}
                                        disabled={isDeleteInProgress}
                                        aria-label="Delete category"
                                        title="Delete"
                                    >
                                        <FiTrash2 size={16} />
                                    </IconBtn>
                                </Actions>

                            </TD>
                        </TR>
                    ))}
                </Table>

                {listStatus === "loading" && (
                    <div style={{ padding: "12px 16px", color: "#6b7280" }}>Loading…</div>
                )}
                {categories.length === 0 && listStatus === "succeeded" && (
                    <div style={{ padding: "12px 16px", color: "#6b7280" }}>No categories yet.</div>
                )}
            </Card>

            {/* ===== Add Modal ===== */}
            {addOpen && (
                <Backdrop onClick={() => setAddOpen(false)}>
                    <Modal onClick={(e) => e.stopPropagation()}>
                        <ModalHeader>
                            <ModalTitle>Add Category</ModalTitle>
                            <CloseIconBtn onClick={() => setAddOpen(false)} aria-label="Close">
                                <FiX size={18} />
                            </CloseIconBtn>
                        </ModalHeader>


                        {createError && <Err style={{ margin: "10px 16px" }}>{createError}</Err>}

                        <ModalBody>
                            <form onSubmit={handleAdd}>
                                <FormGrid>
                                    <Field>
                                        Name
                                        <Input
                                            value={addForm.name}
                                            onChange={(e) => setAddForm(f => ({ ...f, name: e.target.value }))}
                                            required
                                            placeholder="Silver Box"
                                        />
                                        {createErrs["body.name"] && <FieldErr>{createErrs["body.name"]}</FieldErr>}
                                    </Field>
                                    <Field>
                                        Description
                                        <Input
                                            value={addForm.description}
                                            onChange={(e) => setAddForm(f => ({ ...f, description: e.target.value }))}
                                            placeholder="For deposits $50–$99"
                                        />
                                        {createErrs["body.description"] && <FieldErr>{createErrs["body.description"]}</FieldErr>}
                                    </Field>
                                    <Grid2>
                                        <Field>
                                            Min Range
                                            <Input
                                                type="number"
                                                value={addForm.min_range}
                                                onChange={(e) => setAddForm(f => ({ ...f, min_range: e.target.value }))}
                                                required
                                            />
                                            {createErrs["body.min_range"] && <FieldErr>{createErrs["body.min_range"]}</FieldErr>}
                                        </Field>
                                        <Field>
                                            Max Range
                                            <Input
                                                type="number"
                                                value={addForm.max_range}
                                                onChange={(e) => setAddForm(f => ({ ...f, max_range: e.target.value }))}
                                                required
                                            />
                                            {createErrs["body.max_range"] && <FieldErr>{createErrs["body.max_range"]}</FieldErr>}
                                        </Field>
                                    </Grid2>
                                    <Field>
                                        Currency
                                        <Input
                                            value={addForm.currency}
                                            onChange={(e) => setAddForm(f => ({ ...f, currency: e.target.value }))}
                                            required
                                            placeholder="USD"
                                        />
                                        {createErrs["body.currency"] && <FieldErr>{createErrs["body.currency"]}</FieldErr>}
                                    </Field>
                                </FormGrid>

                                <ModalFooter>
                                    <Btn type="button" onClick={() => setAddOpen(false)}>Cancel</Btn>
                                    <Btn variant="primary" type="submit" disabled={isCreateInProgress}>
                                        {isCreateInProgress ? "Creating..." : "Create"}
                                    </Btn>
                                </ModalFooter>
                            </form>
                        </ModalBody>
                    </Modal>
                </Backdrop>
            )}

            {/* ===== Edit Modal ===== */}
            {editOpen && (
                <Backdrop onClick={() => setEditOpen(false)}>
                    <Modal onClick={(e) => e.stopPropagation()}>
                        <ModalHeader>
                            <ModalTitle>Edit Category</ModalTitle>
                            <CloseIconBtn onClick={() => setEditOpen(false)} aria-label="Close">
                                <FiX size={18} />
                            </CloseIconBtn>
                        </ModalHeader>

                        {updateError && <Err style={{ margin: "10px 16px" }}>{updateError}</Err>}

                        <ModalBody>
                            <form onSubmit={handleUpdate}>
                                <FormGrid>
                                    <Field>
                                        Name
                                        <Input
                                            value={editForm.name}
                                            onChange={(e) => setEditForm(f => ({ ...f, name: e.target.value }))}
                                            required
                                        />
                                        {updateErrs["body.name"] && <FieldErr>{updateErrs["body.name"]}</FieldErr>}
                                    </Field>
                                    <Field>
                                        Description
                                        <Input
                                            value={editForm.description}
                                            onChange={(e) => setEditForm(f => ({ ...f, description: e.target.value }))}
                                        />
                                        {updateErrs["body.description"] && <FieldErr>{updateErrs["body.description"]}</FieldErr>}
                                    </Field>
                                    <Grid2>
                                        <Field>
                                            Min Range
                                            <Input
                                                type="number"
                                                value={editForm.min_range}
                                                onChange={(e) => setEditForm(f => ({ ...f, min_range: e.target.value }))}
                                                required
                                            />
                                            {updateErrs["body.min_range"] && <FieldErr>{updateErrs["body.min_range"]}</FieldErr>}
                                        </Field>
                                        <Field>
                                            Max Range
                                            <Input
                                                type="number"
                                                value={editForm.max_range}
                                                onChange={(e) => setEditForm(f => ({ ...f, max_range: e.target.value }))}
                                                required
                                            />
                                            {updateErrs["body.max_range"] && <FieldErr>{updateErrs["body.max_range"]}</FieldErr>}
                                        </Field>
                                    </Grid2>
                                    <Field>
                                        Currency
                                        <Input
                                            value={editForm.currency}
                                            onChange={(e) => setEditForm(f => ({ ...f, currency: e.target.value }))}
                                            required
                                        />
                                        {updateErrs["body.currency"] && <FieldErr>{updateErrs["body.currency"]}</FieldErr>}
                                    </Field>
                                </FormGrid>

                                <ModalFooter>
                                    <Btn type="button" onClick={() => setEditOpen(false)}>Cancel</Btn>
                                    <Btn variant="primary" type="submit" disabled={isUpdateInProgress}>
                                        {isUpdateInProgress ? "Saving..." : "Save Changes"}
                                    </Btn>
                                </ModalFooter>
                            </form>
                        </ModalBody>
                    </Modal>
                </Backdrop>
            )}

            {/* Global confirmation modal (for delete) */}
            <ConfirmationModal />
        </Page>
    );
};

export default GiftCategoriesPage;
